// ./src/components/generic/ColorIconBadge/ColorIconBadge.tsx
//
// A small avatar-style badge that shows an entity's chosen color + icon together.
// When a color is set it renders a colored swatch with a contrasting icon inside;
// otherwise a tinted ThemeIcon fallback. Reusable anywhere an entity carries a
// color and an icon (guest-group sets, guest groups, …).

import React from "react";
import { ColorSwatch, ThemeIcon, MantineRadius } from "@mantine/core";

import { AppIcon } from "../AppIcon";

// A readable icon color on a colored background (dark on light, white on dark).
export const readableIconColor = (hex?: string | null): string => {
  if (!hex) return "#ffffff";
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length < 6) return "#ffffff";
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1a1a1a" : "#ffffff";
};

export interface ColorIconBadgeProps {
  /** Hex color of the entity, or null/empty for the fallback tint. */
  color?: string | null;
  /** AppIcon name chosen for the entity. */
  icon?: string | null;
  /** Diameter in px. */
  size?: number;
  radius?: MantineRadius;
  /** Mantine color for the fallback ThemeIcon when no color is set. */
  fallbackColor?: string;
  /** Icon shown when the entity has no icon of its own (in the fallback tint). */
  fallbackIcon?: string;
}

export const ColorIconBadge: React.FC<ColorIconBadgeProps> = ({
  color,
  icon,
  size = 34,
  radius = "xl",
  fallbackColor = "gray",
  fallbackIcon,
}) => {
  if (color) {
    return (
      <ColorSwatch color={color} size={size} radius={radius}>
        {icon ? <AppIcon iconName={icon} size={Math.round(size * 0.5)} color={readableIconColor(color)} /> : null}
      </ColorSwatch>
    );
  }

  const shownIcon = icon || fallbackIcon;
  return (
    <ThemeIcon variant="light" color={fallbackColor} radius={radius} size={size}>
      {shownIcon ? <AppIcon iconName={shownIcon} size={Math.round(size * 0.55)} /> : null}
    </ThemeIcon>
  );
};
